import type { AxiosInstance } from 'axios';
import axios from 'axios';

import { getAppInsights } from 'analytics/appInsights';
import { getAccessToken } from 'api/authentication/utils/getAccessToken';
import { ENV_VARS } from 'app/config/environment-vars';
import { HTTP_STORE } from 'state/http/httpStore';

import type { OutputOperationResult } from '../outputOperationResult';
import type { EOverrideAccessRights, ProjectTeamMapping } from '../projectTeamConfigurations/client-projectTeamApi';

export enum MappingAssignmentType {
  module = 0,
  propertyOverride = 1,
  relationshipOverride = 2
}

export class ProjectTeamMappingsApi {
  private secureUrl!: string;

  private client!: AxiosInstance;

  constructor() {
    this.secureUrl = `${ENV_VARS.META_URL}/api/projectTeam`;

    this.client = axios.create({
      baseURL: this.secureUrl,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });

    this.client.interceptors.request.use((config) => {
      HTTP_STORE.getState().start('projectTeamMappings');
      const token = getAccessToken();
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.client.interceptors.response.use(
      function (response) {
        HTTP_STORE.getState().stop('projectTeamMappings');
        return response;
      },
      function (error) {
        const appInsights = getAppInsights();
        appInsights.trackException({ exception: new Error(`ProjectTeamMappingsApi ::: ${error.message}`) });

        HTTP_STORE.getState().error('projectTeamMappings', error);
        return Promise.reject(error);
      }
    );
  }

  // ProjectTeamMappings
  public GetAll = async () => {
    return await this.client.get(`/mapping`).then((response) => {
      return response.data as OutputOperationResult<ProjectTeamMapping[]>;
    });
  };

  public CreateProjectTeamMapping = async (projectTeamId: any, templateId: string) => {
    return await this.client
      .post(`/mapping`, { projectTeamId, templateId } as CreateProjectTeamMappingModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public AssignRoleToModule = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    moduleId: string,
    accessRights: EOverrideAccessRights
  ) => {
    return await this.client
      .post(`/mapping/setAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        roleId: projectTeamRoleId,
        resourceId: moduleId,
        mappingAssignmentType: MappingAssignmentType.module,
        accessRights: accessRights
      } as SetProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public UpdateAccessRights = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    moduleId: string,
    accessRights: EOverrideAccessRights
  ) => {
    return await this.client
      .post(`/mapping/setAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        roleId: projectTeamRoleId,
        resourceId: moduleId,
        mappingAssignmentType: MappingAssignmentType.module,
        accessRights: accessRights
      } as SetProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public RemoveModuleAssignment = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    moduleId: string
  ) => {
    return await this.client
      .post(`/mapping/deleteAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        resourceId: moduleId,
        roleId: projectTeamRoleId,
        mappingAssignmentType: MappingAssignmentType.module
      } as DeleteProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public AddPropertyOverrideToRole = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    propertyId: string,
    accessRights: EOverrideAccessRights
  ) => {
    return await this.client
      .post(`/mapping/setAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        roleId: projectTeamRoleId,
        resourceId: propertyId,
        mappingAssignmentType: MappingAssignmentType.propertyOverride,
        accessRights: accessRights
      } as SetProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public ChangePropertyOverrideForRole = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    propertyId: string,
    accessRights: EOverrideAccessRights
  ) => {
    return await this.client
      .post(`/mapping/setAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        roleId: projectTeamRoleId,
        resourceId: propertyId,
        mappingAssignmentType: MappingAssignmentType.propertyOverride,
        accessRights: accessRights
      } as SetProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public RemovePropertyOverrideFromRole = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    propertyId: string
  ) => {
    return await this.client
      .post(`/mapping/deleteAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        resourceId: propertyId,
        roleId: projectTeamRoleId,
        mappingAssignmentType: MappingAssignmentType.propertyOverride
      } as DeleteProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public AddEdgeOverrideToRole = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    relationId: string,
    accessRights: EOverrideAccessRights
  ) => {
    return await this.client
      .post(`/mapping/setAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        roleId: projectTeamRoleId,
        resourceId: relationId,
        mappingAssignmentType: MappingAssignmentType.relationshipOverride,
        accessRights: accessRights
      } as SetProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public ChangeEdgeOverrideForRole = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    relationId: string,
    accessRights: EOverrideAccessRights
  ) => {
    return await this.client
      .post(`/mapping/setAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        roleId: projectTeamRoleId,
        resourceId: relationId,
        mappingAssignmentType: MappingAssignmentType.relationshipOverride,
        accessRights: accessRights
      } as SetProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public RemoveEdgeOverrideFromRole = async (
    projectTeamId: any,
    projectTeamMappingId: any,
    projectTeamRoleId: any,
    relationId: string
  ) => {
    return await this.client
      .post(`/mapping/deleteAssignment`, {
        projectTeamId: projectTeamId,
        mappingId: projectTeamMappingId,
        resourceId: relationId,
        roleId: projectTeamRoleId,
        mappingAssignmentType: MappingAssignmentType.relationshipOverride
      } as DeleteProjectTeamMappingAssignmentModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };

  public DeleteProjectTeamMapping = async (projectTeamId: any, projectTeamMappingId: any) => {
    return await this.client.delete(`${projectTeamId}/mapping/${projectTeamMappingId}`).then((response) => {
      return response.data as OutputOperationResult<ProjectTeamMapping>;
    });
  };

  public MigrateProjectTeamMapping = async (projectTeamMappingId: any, targetTemplateId: string) => {
    return await this.client
      .post(`/mapping/migrate`, {
        targetTemplateId: targetTemplateId,
        originalMappingId: projectTeamMappingId
      } as MigrateProjectTeamMappingModel)
      .then((response) => {
        return response.data as OutputOperationResult<ProjectTeamMapping>;
      });
  };
}

export interface MigrateProjectTeamMappingModel {
  targetTemplateId: string;
  originalMappingId: string;
}

export interface CreateProjectTeamMappingModel {
  templateId: string;
  projectTeamId: number;
}

export interface SetProjectTeamMappingAssignmentModel {
  projectTeamId: string;
  mappingId: string;
  roleId: string;
  resourceId: string;
  mappingAssignmentType: MappingAssignmentType;
  accessRights: EOverrideAccessRights;
}

export interface DeleteProjectTeamMappingAssignmentModel {
  projectTeamId: string;
  mappingId: string;
  resourceId: string;
  roleId: string;
  mappingAssignmentType: MappingAssignmentType;
}
